import React, { useState } from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { assessComplexity, ComplexityLevel } from '../../server/services/probateComplexityScorer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SectionHeader } from '../ui';

interface Props {
    ihtCalc?: IHTCalculation | null;
    assets?: EstateAssets;
    profile?: EstateProfile;
}

const LEVEL_INTERPRETATION: Record<ComplexityLevel, string> = {
    Low: 'A straightforward administration; standard documentation should suffice.',
    Medium: 'Some complicating factors will need careful attention during administration.',
    High: 'Significant complexity; consider professional support for key stages.',
    'Very High': 'Substantial complexity warranting specialist legal and tax advice.',
};

const DEFAULT_VISIBLE = 5;

export const ComplexityScorePanel: React.FC<Props> = ({ ihtCalc, assets, profile }) => {
    const assessment = assessComplexity(ihtCalc, assets, profile);
    const [showAll, setShowAll] = useState(false);

    const visibleFactors = showAll
        ? assessment.factors
        : assessment.factors.slice(0, DEFAULT_VISIBLE);
    const hiddenCount = assessment.factors.length - DEFAULT_VISIBLE;

    return (
        <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
            <div className="px-6 pt-6">
                <SectionHeader
                    numeral=""
                    title="Probate complexity"
                    subtitle="Estimated administration difficulty for this estate"
                />
            </div>

            <div className="px-6 pb-6 space-y-5">
                {/* Score block — large serif numeral + interpretation */}
                <div className="flex items-baseline gap-6 border-b border-[#E8E4DB] pb-5">
                    <div>
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762]">Level</p>
                        <p className="font-serif text-[34px] font-normal text-[#1A1A1A] leading-none mt-1">
                            {assessment.level}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762]">Score</p>
                        <p className="font-mono tabular-nums text-[22px] text-[#1A1A1A] mt-1">
                            {assessment.score}
                        </p>
                    </div>
                </div>

                {/* Italic interpretation line */}
                <div className="font-serif italic text-[15px] leading-relaxed text-[#1A1A1A] pl-4 border-l-2 border-[#3730A3]">
                    {LEVEL_INTERPRETATION[assessment.level]}
                </div>

                {/* Summary */}
                <p className="text-[13px] text-[#3F3D39] leading-relaxed">
                    {assessment.summary}
                </p>

                {/* Factors */}
                {assessment.factors.length > 0 && (
                    <div>
                        <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-2">
                            Factors increasing complexity
                        </h4>
                        <div>
                            {visibleFactors.map((f) => (
                                <div key={f.id} className="flex items-start justify-between gap-3 py-2 border-b border-dotted border-[#E8E4DB]">
                                    <div className="flex-1 min-w-0">
                                        <span className="text-[13px] font-medium text-[#1A1A1A]">{f.label}</span>
                                        {f.detail && (
                                            <div className="text-[12px] text-[#3F3D39] mt-0.5">{f.detail}</div>
                                        )}
                                    </div>
                                    <span className="font-mono tabular-nums text-[12px] text-[#6B6762] shrink-0">+{f.points}</span>
                                </div>
                            ))}
                        </div>

                        {hiddenCount > 0 && (
                            <button
                                onClick={() => setShowAll(!showAll)}
                                className="mt-3 flex items-center gap-1 text-[12px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline transition-colors"
                            >
                                {showAll
                                    ? <><ChevronUp className="w-3 h-3" /> Show fewer</>
                                    : <><ChevronDown className="w-3 h-3" /> Show {hiddenCount} more factor{hiddenCount !== 1 ? 's' : ''}</>}
                            </button>
                        )}
                    </div>
                )}

                {assessment.factors.length === 0 && (
                    <p className="text-[12px] text-[#AFA99F] italic font-serif">
                        No complicating factors detected based on the information provided.
                    </p>
                )}

                <p className="text-[11px] text-[#AFA99F] italic leading-relaxed">
                    This score estimates how difficult the estate may be to administer based on the information entered.
                    It is a workflow aid, not a legal classification.
                </p>
            </div>
        </div>
    );
};
